import { useQuery } from '@tanstack/react-query'
import { http } from '../../utils/httpCommon'
import { apiUrls } from '../../utils/apiUrls'
import { trendGraphApiResponseSchema, TrendGraphApiResponse } from '../../types/sentryIssue.type'
import {
  isDispatchMobileProject,
  isDockAutomationProject,
  SENTRY_PROJECT_APEX_CLIENT,
  SENTRY_PROJECT_DISPATCH_MOBILE,
  SENTRY_PROJECT_DOCK_AUTOMATION,
} from '../../constants/sentryProjects'
import { getWorkstreamCulpritQuery } from '../../utils/workstreamHelper'
import { sentryQueryKeys } from './queryKeys'

interface UseTrendGraphParams {
  projectId?: string
  statsPeriod?: string // e.g., "7d", "14d", "30d"
  severity: 'high' | 'medium' | 'low' | 'total'
  environment?: string | null // Optional - only pass if explicitly selected
  project?: string // Deprecated - use projectId instead
  workstream?: string | null // Optional - for culprit query
}

/**
 * Custom hook to fetch trend graph data from the Sentry API
 * This hook fetches time-series data showing issue counts over a period
 *
 * @param params - Query parameters including stats period, severity, environment, and project
 * @returns React Query result with trend graph data
 */
export const useTrendGraph = ({
  projectId,
  statsPeriod = '7d',
  severity,
  environment,
  project,
  workstream = null,
}: UseTrendGraphParams) => {
  // Normalize environment and workstream for consistent cache keys
  const normalizedEnvironment = environment && environment !== 'all' ? environment : null
  const normalizedWorkstream = workstream && workstream !== 'all' ? workstream : null
  // Support both projectId (new) and project (legacy) parameters
  const effectiveProject =
    workstream === 'dispatch-mobile'
      ? SENTRY_PROJECT_DISPATCH_MOBILE
      : workstream === 'dock-automation'
        ? SENTRY_PROJECT_DOCK_AUTOMATION
        : (projectId ?? project ?? SENTRY_PROJECT_APEX_CLIENT)

  return useQuery({
    queryKey: sentryQueryKeys.trendGraph({
      projectId: effectiveProject,
      statsPeriod,
      severity,
      environment: normalizedEnvironment,
      workstream: normalizedWorkstream,
    }),
    queryFn: async (): Promise<TrendGraphApiResponse> => {
      // Build query params
      const params = new URLSearchParams()
      params.append('statsPeriod', statsPeriod)
      params.append('severity', severity)

      // Only add environment if it's explicitly provided (normalized value)
      if (normalizedEnvironment) {
        params.append('environment', normalizedEnvironment)
      }

      params.append('project', effectiveProject)

      if (!isDispatchMobileProject(effectiveProject) && !isDockAutomationProject(effectiveProject)) {
        // Only add culprit query if workstream is explicitly provided (not null)
        if (normalizedWorkstream) {
          const culpritQuery = getWorkstreamCulpritQuery(normalizedWorkstream)
          if (culpritQuery) {
            params.append('query', culpritQuery)
          }
        }
      }

      const apiEndpoint = `${apiUrls.getTrendGraph}?${params.toString()}`
      const response = await http.get(apiEndpoint)
      const data = response.data
      // Validate response with Zod schema
      const parsed = trendGraphApiResponseSchema.parse(data)
      return parsed
    },
    // Prevent unnecessary refetches
    refetchOnWindowFocus: false,
    refetchOnReconnect: false, // Don't refetch on network reconnect
    // Cache data for 5 minutes (data won't be considered stale)
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    // Retry failed requests
    retry: 1,
  })
}
